// Dip: the "places left this month" figure, kept live and relevant while the shopper is on the page.
//
// Charlie 2026-09-06/07: the scarcity must be real, must tick in real time, and must count the
// products actually being bought (general wording until products are chosen). Server truth:
// snippets/dip-offer-places-count.liquid (metafields, else live Shopify stock minus the subscriber
// reserves), rendered by snippets/dip-offer-places.liquid with per-product figures in data-per.
// This script (1) re-reads that count from templates/product.places.liquid every 20 seconds and
// (2) listens for `dip:places-products` (the buy modules announce the product keys in the chosen
// plan) and shows the tightest figure among THOSE products. A figure only falls when stock or a
// metafield actually moved; nothing decrements on a timer. At zero the box hides.

const POLL_MS = 20000;
/* Round 13 (Charlie 2026-09-08, "ticking so customers can see it going down"): between real reads the figure
   advances at the measured rate (places per ms, from the reads of this session; seeded by data-rate, places per hour,
   a theme setting), re-syncing to the truth on every read. Guard rails: it never runs more than DRIFT_MAX places
   ahead of the last real figure, and it is never painted ABOVE the truth (an over-run simply holds until the
   truth catches up). A rate of 0 = the old behaviour, movement only when stock really fell. */
const DRIFT_MAX = 3;
/* Round 15 (Charlie 2026-09-08, "the top number said 10,842 and the bottom 10,843"): ONE clock per page. Every places
   box (the strip's pill, the plan panel) paints the same shared figure; the truth, its read time, the rate and the
   projected figure live here, not per box. (Per-box state let the panel re-render the raw truth on a poll while the
   pill kept its projection, one apart.) */
const shared = { set: null, truth: null, truthAt: 0, shown: null, rate: 0 };
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const fmt = (n) => Number(n).toLocaleString('en-GB');
/* Clock form (Charlie 2026-09-08): the count as digit tiles, the thousands comma between them. */
const clockHtml = (n) => fmt(n).split('').map((ch) => (ch === ',' ? '<span class="dip-places__comma" aria-hidden="true">,</span>' : `<span class="dip-places__digit">${ch}</span>`)).join('');
const paint = (el, n) => { if (el.classList.contains('dip-places__clock')) el.innerHTML = clockHtml(n); else el.textContent = fmt(n); };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const parsePer = (str) => Object.fromEntries(String(str || '').split(',').filter(Boolean).map((p) => { const [k, v] = p.split('='); return [k.trim(), parseInt(v, 10)]; }));

function figureFor(host, keys) {
  const per = parsePer(host.dataset.per);
  const vals = (keys || []).map((k) => per[k]).filter((v) => Number.isFinite(v));
  return vals.length ? Math.min(...vals) : null;
}

function animateTo(el, from, to) {
  el.dataset.n = String(to);
  if (REDUCED.matches || from === to || !Number.isFinite(from)) { paint(el, to); return; }
  const start = performance.now(); const dur = 900;
  const tick = (now) => {
    const p = Math.min(1, (now - start) / dur); const e = 1 - Math.pow(1 - p, 3);
    paint(el, Math.round(from + (to - from) * e));
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

const pulse = (el, cls) => { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); };

/* The page's truth: the first box that names products gives the set and the tightest figure for it (every box
   carries the same data-per, and setProducts gives every box the same set). */
function currentTruth() {
  const hosts = [...document.querySelectorAll('[data-places]')];
  const host = hosts.find((h) => (h.dataset.products || '').trim()) || hosts[0];
  if (!host) return { left: null, set: '' };
  const set = host.dataset.products || '';
  const keys = set.split(',').map((k) => k.trim()).filter(Boolean);
  return { left: keys.length ? figureFor(host, keys) : null, set };
}

/* Take a fresh truth into the shared clock (round 13 rules, now in one place): the rate is measured from falls
   between reads (seeded by data-rate, places per hour); the shown figure never sits above the truth, never more
   than DRIFT_MAX below it, and holds when the truth catches up with a projection rather than jumping back up.
   An unchanged read keeps the read time, so the projection keeps advancing between reads. */
function syncTruth(left, set) {
  const now = Date.now();
  const sameSet = shared.set === set;
  shared.set = set;
  if (left === null) { shared.truth = null; shared.shown = null; shared.truthAt = 0; return; }
  if (sameSet && Number.isFinite(shared.truth) && shared.truthAt) {
    if (left < shared.truth) {
      const measured = (shared.truth - left) / Math.max(1, now - shared.truthAt);
      shared.rate = shared.rate ? shared.rate * 0.5 + measured * 0.5 : measured;
      shared.truth = left; shared.truthAt = now;
    } else if (left > shared.truth) { shared.truth = left; shared.truthAt = now; }
  } else { shared.truth = left; shared.truthAt = now; }
  if (!shared.rate) {
    const host = document.querySelector('[data-places]');
    const perHour = Number(host?.dataset.rate) || 0;
    if (perHour > 0) shared.rate = perHour / 3600000;
  }
  if (!sameSet || !Number.isFinite(shared.shown) || shared.shown > shared.truth || shared.truth - shared.shown > DRIFT_MAX) shared.shown = shared.truth;
}

// Rebuild the sentence for the host's current product set: kicker · count · why, or kicker · why
// (general) while no product is chosen. The figure painted is the SHARED shown figure.
function renderHost(host) {
  const body = host.querySelector('[data-places-body]');
  if (!body) return;
  const keys = (host.dataset.products || '').split(',').map((k) => k.trim()).filter(Boolean);
  const truth = keys.length ? figureFor(host, keys) : null;
  const left = truth === null ? null : (Number.isFinite(shared.shown) && shared.set === host.dataset.products ? shared.shown : truth);
  const kicker = host.dataset.kicker || '';
  const why = host.dataset.why || '';
  const sep = '<span class="dip-places__sep" aria-hidden="true"> · </span>';
  if (keys.length && truth !== null && truth <= 0) { host.hidden = true; return; }
  host.hidden = false;
  const prevFig = body.querySelector('[data-places-left]');
  const prev = prevFig ? parseInt(prevFig.dataset.n, 10) : NaN;
  // Half now, half later, arm B (html[data-dip-hh], snippets/dip-arm.liquid): its own wording ("half-price trials").
  const hh = document.documentElement.hasAttribute('data-dip-hh') && host.dataset.tplHh;
  const tpl = hh || host.dataset.tpl || 'Only [left] half-price first orders left this month';
  let html = '';
  if (host.dataset.clock) {
    // Panel form: the kicker sits in the panel head (server-rendered); the body is the count as digit
    // tiles with the sentence's tail beside it, then the why line.
    if (left !== null) {
      const after = (tpl.split('[left]')[1] || '').trim();
      html += `<span class="dip-places__count"><b class="dip-places__clock" data-places-left data-n="${left}">${clockHtml(left)}</b><span class="dip-places__after">${esc(after)}</span></span>`;
    }
    if (why) html += `<span class="dip-places__why">${esc(why)}</span>`;
  } else {
    // Round 15: no separator dangles after the kicker; a pill with only a kicker to show hides.
    const hasTail = left !== null || !!why;
    html = kicker ? `<b class="dip-places__kicker">${esc(kicker)}</b>${hasTail ? sep : ''}` : '';
    if (left !== null) {
      html += esc(tpl).replace('[left]', `<b data-places-left data-n="${left}">${fmt(left)}</b>`) + (why ? sep : '');
    }
    if (why) html += `<span class="dip-places__why">${esc(why)}</span>`;
    if (!hasTail) { body.innerHTML = html; host.hidden = true; return; }
  }
  body.innerHTML = html;
  // Animate only when the SAME product set's figure moved (a poll found stock fell). A change of
  // products switches the figure instantly: counting from one product's number to another's
  // would look like stock draining when it is not.
  const sameSet = host.dataset.shownFor === host.dataset.products;
  host.dataset.shownFor = host.dataset.products;
  const fig = body.querySelector('[data-places-left]');
  if (fig && sameSet && Number.isFinite(prev) && prev !== left) {
    animateTo(fig, prev, left);
    pulse(host, 'is-moved');
  }
}

/* Round 13: once a second, advance the shared figure at its measured rate, within the drift budget, and paint it
   in every box. Whole places only: the figure falls by one each time a full place's worth of time has passed
   (a floor of the raw projection showed the truth minus one within a second of loading). */
function tickProjection() {
  if (!Number.isFinite(shared.truth) || !shared.rate || !Number.isFinite(shared.shown)) return;
  const expected = shared.truth - Math.floor(shared.rate * (Date.now() - shared.truthAt));
  const target = Math.max(expected, shared.truth - DRIFT_MAX, 0);
  if (target >= shared.shown) return;
  shared.shown = target;
  document.querySelectorAll('[data-places]').forEach((host) => {
    if ((host.dataset.products || '') !== shared.set) return;
    const fig = host.querySelector('[data-places-left]');
    if (!fig) return;
    paint(fig, target);
    fig.dataset.n = String(target);
    if (fig.classList.contains('dip-places__clock')) pulse(fig, 'is-tick');
    pulse(host, 'is-moved');
  });
}

function renderAll() {
  const { left, set } = currentTruth();
  syncTruth(left, set);
  document.querySelectorAll('[data-places]').forEach(renderHost);
}

function setProducts(keys) {
  document.querySelectorAll('[data-places]').forEach((host) => { host.dataset.products = (keys || []).join(','); });
  renderAll();
}

function applyCount(text) {
  const parts = String(text || '').trim().split('|');
  const per = parts[4] || '';
  if (!per) return;
  document.querySelectorAll('[data-places]').forEach((host) => { host.dataset.per = per; });
  renderAll();
}

let timer = null;
async function poll() {
  const host = document.querySelector('[data-places]');
  const url = host?.dataset.poll;
  if (!url || document.visibilityState === 'hidden') return;
  try {
    const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'text/plain' } });
    if (res.ok) applyCount(await res.text());
  } catch { /* a failed poll leaves the rendered number standing */ }
}
function start() {
  if (timer || !document.querySelector('[data-places]')) return;
  timer = setInterval(poll, POLL_MS);
  setInterval(tickProjection, 1000);
  // Seed the shared clock from the server-rendered figures (no repaint: the markup already shows the truth).
  { const { left, set } = currentTruth(); syncTruth(left, set); document.querySelectorAll('[data-places]').forEach((h) => { h.dataset.shownFor = h.dataset.products; }); }
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') poll(); });
  // The buy modules announce the products in the chosen plan (dip-simple-buy.js, dip-routine.js).
  document.addEventListener('dip:places-products', (e) => setProducts(e.detail?.keys || []));
  if (window.__dipPlacesKeys) setProducts(window.__dipPlacesKeys);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
